// Added by Sejune Oh on 2019-02-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ElementTimeStampProps } from '@/helper/define'
import { getQueryPath } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import RootStore from '../../index'
import {
    reqGetGroup,
    reqGetListGroups,
    reqGetAssignedUsersToGroup,
    reqGetSingleAssignedUsersToGroup,
    reqAssignUsersToGroup,
    reqCreateGroup,
    reqDeleteGroup,
    reqUpdateGroup,
} from './GroupStore.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface GroupElementProps extends ElementTimeStampProps {
    id?: string
    group_key?: string
    description?: string
    assgiend_fk_user_ids?: Array<string>
}

export interface DisplableGroupElementProps extends GroupElementProps {
    number_of_users?: string
    number_of_datastorage?: number
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class GroupStore {
    elements: Array<GroupElementProps | DisplableGroupElementProps> = []
    totalCount: number = 0
    root?: RootStore

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    constructor(root: RootStore) {
        this.root = root
        makeAutoObservable(this)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    getElement = async (id: string) => {
        const { data: rtnElement } = await reqGetGroup(id)
        return rtnElement
    }

    getElements = async (path: string) => {
        const {
            data: { total_count, items },
        } = await reqGetListGroups(path)
        this._setTotalCount(parseInt(total_count!))
        return items!
    }

    addElement = async (element: GroupElementProps) => {
        const { data: rtnElement } = await reqCreateGroup(element)
        return rtnElement
    }

    removeElement = async (id: string) => {
        return await reqDeleteGroup(id)
    }

    updateElement = async (id: string, element: GroupElementProps) => {
        const { data: updatedElement } = await reqUpdateGroup(id, element)
        return updatedElement.id
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    checkDuplicatedGroupKey = async (groupKey: string) => {
        const filter = { $op: 'and', group_key: groupKey }
        const path = getQueryPath({ pageIndex: 0, orderBy: 'group_key', filter: JSON.stringify(filter) })

        const elements = await this.getElements(path)
        return elements.length > 0
    }

    getAssignedUsers = async (id: string) => {
        const {
            data: { items },
        } = await reqGetAssignedUsersToGroup(id)
        return items!
    }

    getSingleAssignedUsers = async (id: string) => {
        const {
            data: { items },
        } = await reqGetSingleAssignedUsersToGroup(id)
        return items!
    }

    updateAssignedUsers = async (id: string, userIds: Array<string>) => {
        return await reqAssignUsersToGroup(id, userIds)
    }

    getGroupKey = (groupId: string) => {
        const groupElement = this.elements.find(({ id }) => id === groupId)!
        if (!groupElement) return null

        return groupElement.group_key
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    _setTotalCount = (count: number) => {
        this.totalCount = count
    }

    setElements = (elements: Array<GroupElementProps>) => {
        this.elements = elements
    }

    listWithAssignedCount = async (elements: Array<GroupElementProps>, groupNdataStorageMap: Map<string, number>) => {
        const assignedUserCountedElement: Array<DisplableGroupElementProps> = []
        for (let i = 0, max = elements.length; i < max; i++) {
            const element = elements[i]
            const assignedUsers = await this.getAssignedUsers(element.id!)
            assignedUserCountedElement.push({
                ...element,
                number_of_users: assignedUsers.length.toString(),
                number_of_datastorage: groupNdataStorageMap.get(element.id!) || 0,
            })
        }

        this.elements = assignedUserCountedElement
    }
}
